import {
  CollectionRecord,
  ContentRecord,
  Enums,
  ExpandedHuntPackageRecord,
  HuntPackageRecord,
  ThreatProfile,
  Tool,
} from '@cyborg-monorepo/hunter/common'
import { SupportedTools, toolFactory } from '@cyborg-monorepo/hunter/matchboi'
import { BadgeVariant } from '@hyperkit'

import {
  FacetButtonItem,
  FacetButtonType,
} from '../modules/shared/components/facet-buttons/facet-buttons.component'

/**
 * Compare if the 2 objects are exactly the value objects.
 * @param object1
 * @param object2
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function deepEqual(object1: any, object2: any): boolean {
  const keys1 = Object.keys(object1)
  const keys2 = Object.keys(object2)

  if (keys1.length !== keys2.length) {
    return false
  }

  for (const key of keys1) {
    const val1 = object1[key]
    const val2 = object2[key]
    const areObjects =
      val1 != null &&
      typeof val1 === 'object' &&
      val2 != null &&
      typeof val2 === 'object'
    if (
      (areObjects && !deepEqual(val1, val2)) ||
      (!areObjects && val1 !== val2)
    ) {
      return false
    }
  }

  return true
}

/**
 * Converts the provided tags into facet button items
 * @param tags
 */
export function gatherTags(data: {
  tags: CollectionRecord | HuntPackageRecord | ThreatProfile['tags']
}): FacetButtonItem[] {
  const buttons: FacetButtonItem[] = []

  for (const key in Enums.CyborgTagTypeEnum) {
    const keyValue = Enums.CyborgTagTypeEnum[key]
    if (data?.tags && data.tags[keyValue]) {
      const itemValues: string[] = data.tags[keyValue]
      itemValues.forEach((itemValue) => {
        buttons.push({
          type: FacetButtonType.tag,
          category: keyValue,
          value: itemValue,
        })
      })
    }
  }
  return buttons
}

/**
 * Determines if a given hunt package is able to be used in the hunt module on the basis of whether or not it has at least one content record that has a hunt goal
 * @param huntPackage
 */
export function isHuntPackageHuntCompatible(
  huntPackage: ExpandedHuntPackageRecord | HuntPackageRecord,
): boolean {
  if (!huntPackage.content.records || !huntPackage.content.records.length)
    return false
  return huntPackage.content.records.some((contentRecord) => {
    return contentRecord.goal.toLowerCase() === 'hunt'
  })
}

/**
 * Determines if a given content record is able to be used in the hunt module on the basis of whether or not it has a behavior goal
 * @param huntPackage
 */
export function isContentHuntCompatible(record: ContentRecord): boolean {
  return record.goal.toLowerCase() != 'behavior'
}

// ECharts renders to <canvas>, so it needs literal color values, not
// var(...) strings -- this reads the currently resolved value of a semantic
// accent token so these status colors reflect the active theme instead of
// hardcoding one mode's hex.
function accentColor(token: string): string {
  return getComputedStyle(document.documentElement)
    .getPropertyValue(token)
    .trim()
}

export function $warning(): string {
  return accentColor('--foreground-accent-severe')
}
export function $error(): string {
  return accentColor('--foreground-accent-critical')
}
export function $success(): string {
  return accentColor('--foreground-accent-positive')
}
export function $info(): string {
  return accentColor('--foreground-accent-info')
}
export function $base(): string {
  return accentColor('--foreground-muted')
}

export function mapOutcomeColor(outcome: Enums.OutcomeEnum): string {
  if (outcome == Enums.OutcomeEnum.DETECTION) return $warning()
  if (outcome == Enums.OutcomeEnum.ESCALATED) return $error()
  if (outcome == Enums.OutcomeEnum.NO_FINDINGS) return $success()
  if (outcome == Enums.OutcomeEnum.VISIBILITY_GAP) return $warning()
  else return $base()
}

export function mapStatusColor(outcome: Enums.StatusEnum): string {
  if (outcome == Enums.StatusEnum.TO_DO) return $success()
  if (outcome == Enums.StatusEnum.IN_PROGRESS) return $warning()
  if (outcome == Enums.StatusEnum.DONE) return $error()
  else return $base()
}

export function mapPriorityColor(outcome: Enums.PriorityEnum): string {
  if (outcome == Enums.PriorityEnum.LOW) return $success()
  if (outcome == Enums.PriorityEnum.MEDIUM) return $warning()
  if (outcome == Enums.PriorityEnum.HIGH) return $error()
  else return $base()
}

export function mapSeverityToBadgeVariant(severity: string): BadgeVariant {
  if (severity == Enums.SeverityEnum.LOW) return 'success'
  if (severity == Enums.SeverityEnum.MEDIUM) return 'warning'
  if (severity == Enums.SeverityEnum.MEDIUM) return 'warning'
  if (severity == Enums.SeverityEnum.HIGH) return 'error'
  if (severity == Enums.SeverityEnum.CRITICAL) return 'secondary'
  else return 'information'
}

/**
 * Returns the tool icon associated with the provided tool name
 * @param tool
 */
export function getToolIcon(tool: string): string {
  // Slugify tool name, collapsing runs of dashes (e.g. " - " → "-")
  const slug = tool
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
  return `/assets/img/icons/cyborg-tool-${slug}.png`
}

/**
 * Determines if a given tool supports url opening based on whether or not it has implemented supportsSearchPermalinks
 * @param tool
 */
export function supportedToolUrl(tool: SupportedTools): boolean {
  return toolFactory(tool).supportsSearchPermalinks()
}

/**
 * Determines if a given tool supports pivoting based on whether or not it has implemented pivotEscape
 * @param tool
 */
export function supportedPivot(tool: SupportedTools): boolean {
  return toolFactory(tool).supportsPivot()
}

/**
 * scrolls to element on page using element id
 * @param target
 */
export function scrollTo(target: string): void {
  document.getElementById(target).scrollIntoView({
    behavior: 'smooth',
    block: 'start',
    inline: 'nearest',
  })
}

/**
 * Returns user's tool root URL for the given tool, or returns null if not set
 */
export function getSearchURLForContent(tool: Tool, content: string): string {
  return toolFactory(tool.product).generateSearchPermalink(
    tool.rootUrl,
    content,
  )
}

export function addNewLinesBetweenWords(target: string): string {
  const wordsArray = target.split(' ')
  const resultString = wordsArray.join('\n')
  return resultString
}

// Define a type for enum-like objects
type EnumLike = { [key: string]: string | number }

/**
 * Sorts an array of enum values according to their definition order.
 * @param enumObject The enum object to use for ordering.
 * @param values An array of enum values to be sorted.
 * @returns A new array with the enum values sorted.
 */
export function sortEnumValues<T extends EnumLike>(
  enumObject: T,
  values: Array<T[keyof T]>,
): Array<T[keyof T]> {
  const enumEntries = Object.entries(enumObject).filter(([key, value]) =>
    isNaN(Number(key)),
  ) as Array<[string, T[keyof T]]>

  const orderMap = new Map(
    enumEntries.map(([key, value], index) => [value, index]),
  )

  return [...values].sort((a, b) => {
    const orderA = orderMap.get(a)
    const orderB = orderMap.get(b)

    if (orderA === undefined || orderB === undefined) {
      throw new Error('Invalid enum value encountered during sorting')
    }

    return orderA - orderB
  })
}

/**
 * Safely formats a date input as a localized, human-readable string.
 * Returns empty string if input is null, undefined, or invalid.
 *
 * @param input Date string, number, or Date object to format.
 * @returns Formatted date string or empty string if invalid.
 */
export function formatSafeDate(
  input: string | number | Date | undefined,
): string {
  if (!input) return ''
  const date = new Date(input)
  return !isNaN(date.getTime())
    ? date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : ''
}
